import { useEffect, useState } from 'react';

import { GALLERY_GRID_MIN_WIDTH } from './constants/layout';

/**
 * Whether the gallery is currently laid out as a grid of tiles rather than a swipe carousel.
 *
 * The layout itself is decided in CSS, so this is deliberately not what puts the grid on
 * screen. It exists for the two things a stylesheet cannot decide: whether to mount the video
 * player, and which slides belong in the tab order. It matches the same query the stylesheet
 * uses, rather than reading `window.innerWidth`, so it cannot disagree with what is rendered
 * over the scrollbar's width at the boundary.
 *
 * `undefined` until measured on the client. Both callers treat that as "not the carousel", so a
 * video never autoplays and no slide is dropped from the tab order on the strength of a guess.
 */
export const useIsGridLayout = (allowGridLayout = false): boolean | undefined => {
  // A consumer that never shows a grid knows the answer without measuring, on the server too.
  const [isGridLayout, setIsGridLayout] = useState<boolean | undefined>(
    allowGridLayout ? undefined : false,
  );

  useEffect(() => {
    if (!allowGridLayout) {
      setIsGridLayout(false);

      return;
    }

    const query = window.matchMedia(`(min-width: ${GALLERY_GRID_MIN_WIDTH}px)`);
    const update = () => setIsGridLayout(query.matches);

    update();
    query.addEventListener('change', update);

    return () => query.removeEventListener('change', update);
  }, [allowGridLayout]);

  return isGridLayout;
};
